import type { FetchResult } from '@apollo/client';

import { useResponseHandler } from '@hooks/useResponseHandler';
import { useSession } from '@hooks/useSession';

import {
  useAssignUserToPatientMutation,
  useGetVitallPatientRecordQuery,
  useUpdateEmergencyAccessMutation,
  useUpdatePatientProfileStatusMutation,
} from 'generated/vitall-patient.generated';
import type {
  AssignUserToPatientMutation,
  AssignUserToPatientMutationVariables,
  UpdateEmergencyAccessMutation,
  UpdateEmergencyAccessMutationVariables,
  UpdatePatientProfileStatusMutation,
  UpdatePatientProfileStatusMutationVariables,
} from 'generated/vitall-patient.generated';

export const useGetVitallPatient = (params: { id?: string | null }) => {
  const { handleError } = useResponseHandler();
  const {
    state: { isCurator, isPractitioner },
  } = useSession();

  const vitall_patient_id = params.id;
  const response = useGetVitallPatientRecordQuery({
    variables: {
      input: {
        vitall_patient_id,
      },
      is_curator: isCurator,
      is_practitioner: isPractitioner,
    },
    skip: !vitall_patient_id,
  });
  if (response.error) {
    handleError(response.error);
  }

  return response;
};

export const useGetVitallPatientByUserId = (params?: { id?: string }) => {
  const {
    state: { activeProfile },
  } = useSession();
  const user_id = params?.id ?? activeProfile?.user_id;
  const response = useGetVitallPatientRecordQuery({
    variables: {
      input: {
        vitall_patient_id: undefined,
        user_id,
      },
    },
    skip: !user_id,
  });

  return response;
};

export function useAssignUserToPatient(): [
  (
    params: AssignUserToPatientMutationVariables & {
      old_curator_user_id?: string | null;
    }
  ) => Promise<FetchResult<AssignUserToPatientMutation>>,
  boolean,
] {
  const { handleError } = useResponseHandler();
  const [mutate, { loading: baseLoading }] = useAssignUserToPatientMutation();
  const loading = baseLoading;

  async function updateVitallPatient(
    params: AssignUserToPatientMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
    });
    if (response.errors) {
      handleError(response.errors);
    }
    return response;
  }
  return [updateVitallPatient, loading];
}

export function useUpdateEmergencyAccess(): [
  (
    params: UpdateEmergencyAccessMutationVariables
  ) => Promise<FetchResult<UpdateEmergencyAccessMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] = useUpdateEmergencyAccessMutation();
  const loading = baseLoading;

  async function updateEmergencyAccess(
    params: UpdateEmergencyAccessMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
    });
    return response;
  }
  return [updateEmergencyAccess, loading];
}

export function useUpdatePatientProfileStatus(): [
  (
    params: UpdatePatientProfileStatusMutationVariables
  ) => Promise<FetchResult<UpdatePatientProfileStatusMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] =
    useUpdatePatientProfileStatusMutation();
  const loading = baseLoading;

  async function updateProfileStatus(
    params: UpdatePatientProfileStatusMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
      notifyOnNetworkStatusChange: false,
    });
    return response;
  }
  return [updateProfileStatus, loading];
}
